import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ChatWidget = () => {
  const location = useLocation();
  const isInternalRoute = location.pathname.startsWith('/sales') || location.pathname.startsWith('/admin');

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hey there! I\'m the AI assistant for North Texas Welding and Fabrication. I can help you get a quick estimate for fencing, concrete, metal buildings, and more. What can I help you with today?' }
  ]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [leadData, setLeadData] = useState({});
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  const extractLeadInfo = useCallback((text) => {
    const update = {};
    const phoneMatch = text.match(/\b(\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4})\b/);
    if (phoneMatch) update.phone = phoneMatch[1];
    const emailMatch = text.match(/\b[\w.+-]+@[\w-]+\.[\w.-]+\b/);
    if (emailMatch) update.email = emailMatch[1] || emailMatch[0];
    if (Object.keys(update).length > 0) {
      setLeadData(prev => ({ ...prev, ...update }));
    }
  }, []);

  const saveLead = useCallback(async (sid) => {
    if (!leadData.phone && !leadData.email) return;
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API}/api/chat/lead`);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.send(JSON.stringify({ session_id: sid || sessionId, ...leadData }));
    } catch {
      // Lead save failed silently — server logs capture the error
    }
  }, [leadData, sessionId]);

  const sendMessage = useCallback(async () => {
    if (!input.trim() || isStreaming) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setIsStreaming(true);

    extractLeadInfo(userMsg);

    try {
      const xhr = new XMLHttpRequest();
      let currentSessionId = sessionId;

      // If no session, create one inline
      if (!currentSessionId) {
        const sessXhr = new XMLHttpRequest();
        sessXhr.open('POST', `${API}/api/chat/session`, false);
        sessXhr.setRequestHeader('Content-Type', 'application/json');
        try { sessXhr.send('{}'); } catch { /* postMessage */ }
        if (sessXhr.status === 200) {
          const data = JSON.parse(sessXhr.responseText);
          currentSessionId = data.session_id;
          setSessionId(currentSessionId);
        }
      }

      xhr.open('POST', `${API}/api/chat/message`);
      xhr.setRequestHeader('Content-Type', 'application/json');

      let assistantText = '';
      let buffer = '';

      // Add empty assistant message to stream into
      setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

      xhr.onprogress = function () {
        const newData = xhr.responseText.substring(buffer.length);
        buffer = xhr.responseText;

        const lines = newData.split('\n');
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.substring(6));
            if (data.token) {
              assistantText += data.token;
              setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: 'assistant', content: assistantText };
                return updated;
              });
            }
            if (data.done) {
              setIsStreaming(false);
              // Auto-save lead data periodically
              if (leadData.phone || leadData.email) {
                saveLead(currentSessionId);
              }
            }
            if (data.error) {
              setIsStreaming(false);
              setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: 'assistant', content: 'Sorry, I had a connection issue. Please try again or call us directly at 817-723-5006.' };
                return updated;
              });
            }
          } catch {
            // Partial JSON line — continue
          }
        }
      };

      xhr.onloadend = function () {
        setIsStreaming(false);
      };

      xhr.onerror = function () {
        setIsStreaming(false);
        setMessages(prev => {
          const updated = [...prev];
          if (updated[updated.length - 1]?.role === 'assistant' && !updated[updated.length - 1]?.content) {
            updated[updated.length - 1] = { role: 'assistant', content: 'Sorry, I\'m having trouble connecting. Please call us at 817-723-5006 for immediate assistance.' };
          }
          return updated;
        });
      };

      try {
        xhr.send(JSON.stringify({ session_id: currentSessionId, message: userMsg }));
      } catch {
        // postMessage error on production — XHR still fires
      }
    } catch {
      setIsStreaming(false);
    }
  }, [input, isStreaming, sessionId, extractLeadInfo, leadData, saveLead]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (isInternalRoute) return null;

  return (
    <>
      {/* Chat Bubble */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-red-600 hover:bg-red-700 rounded-full shadow-2xl flex items-center justify-center transition-all hover:scale-110 group"
          data-testid="chat-widget-trigger"
          aria-label="Open chat"
        >
          <MessageCircle className="text-white" size={28} />
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse"></span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-4 right-4 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-2rem)] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-zinc-200" data-testid="chat-widget-window">
          {/* Header */}
          <div className="bg-zinc-900 px-5 py-4 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-red-600 rounded-full flex items-center justify-center">
                <MessageCircle size={18} className="text-white" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm">NTX Welding Assistant</p>
                <p className="text-green-400 text-xs flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full inline-block"></span> Online 24/7
                </p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-zinc-400 hover:text-white transition-colors" data-testid="chat-close-btn" aria-label="Close chat">
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-zinc-50">
            {messages.map((msg, idx) => (
              <div key={`msg-${idx}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                  msg.role === 'user'
                    ? 'bg-red-600 text-white rounded-br-sm'
                    : 'bg-white text-zinc-800 border border-zinc-200 rounded-bl-sm shadow-sm'
                }`}>
                  {msg.content}
                  {msg.role === 'assistant' && isStreaming && idx === messages.length - 1 && (
                    <span className="inline-block w-1.5 h-4 bg-red-600 ml-0.5 animate-pulse rounded-sm"></span>
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-4 py-3 bg-white border-t border-zinc-200 flex-shrink-0">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your message..."
                className="flex-1 bg-zinc-100 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-red-600/30 border border-zinc-200 focus:border-red-600 transition-all"
                disabled={isStreaming}
                data-testid="chat-input"
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || isStreaming}
                className="w-10 h-10 bg-red-600 hover:bg-red-700 disabled:bg-zinc-300 rounded-xl flex items-center justify-center transition-colors flex-shrink-0"
                data-testid="chat-send-btn"
                aria-label="Send message"
              >
                {isStreaming ? (
                  <Loader2 size={18} className="text-white animate-spin" />
                ) : (
                  <Send size={18} className="text-white" />
                )}
              </button>
            </div>
            <p className="text-[10px] text-zinc-400 text-center mt-2">
              Call us anytime: <a href="tel:8177235006" className="text-red-600 hover:underline">817-723-5006</a>
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
